import { apiFetch } from '../../_services/apiFetch';
import { ApprovalDecisionActions } from '../../_components/Shared/ApprovalDecisionActions';
import React from 'react';
import { redirectUnlessInventory } from '../../utils/moduleAccess';
import { Alert, Form, FormGroup, FormControl, Button, Row, Col } from 'react-bootstrap';
import SharedDataTable from '../../_components/Shared/SharedDataTable';
import config from '../../../config';
import moment from 'moment';
import { handleFocusInScrollField } from './invKeyboardScroll';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';
import {
    InventorySummaryExportTable,
    SCRAPPING_SUMMARY_EXPORT_COLUMNS,
    exportInventorySummary,
    mapScrappingToExportRow,
} from './invSummaryExport';

function normalizeDateFilterInput(input) {
    const raw = String(input || '').trim();
    if (!raw) return '';
    const ddmmyyyy = moment(raw, 'DD/MM/YYYY', true);
    if (ddmmyyyy.isValid()) return ddmmyyyy.format('YYYY-MM-DD');
    const iso = moment(raw, 'YYYY-MM-DD', true);
    if (iso.isValid()) return iso.format('YYYY-MM-DD');
    return '';
}

function statusLabelScrap(status, rejectionReason) {
    const st = String(status || '').toLowerCase();
    if (st === 'rejected') return `Rejected. ${rejectionReason || 'No reason provided'}`;
    if (st === 'approved' || st === 'confirmed') return 'Approved';
    if (st === 'pending_approval' || st === 'pending_confirmation' || st === 'pending') {
        return 'Pending Approval';
    }
    return status || 'Pending';
}

function formatDateTime(value) {
    if (!value) return '-';
    const m = moment(value);
    return m.isValid() ? m.format('DD/MM/YYYY HH:mm:ss') : String(value);
}

class ScrappingSummary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            data: [],
            loading: false,
            error: null,
            company: 0,
            searchKeyword: '',
            searchSubmittedDate: '',
        };
    }

    componentDidMount() {
        this.loadUserData();
    }

    loadUserData = () => {
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            if (userData && userData.accountId) {
                this.setState({ company: userData.accountId }, () => {
                    this.loadScrapData();
                });
            }
        } catch (error) {
            console.error('Error loading user data:', error);
        }
    };

    loadScrapData = async (decisionSaved = false) => {
        if (!this.state.company) return;

        this.setState({ loading: true, error: null });
        try {
            const response = await apiFetch(
                `${config.apiUrl}/getInventoryApprovalList`,
                { method: 'GET', headers: { 'Content-Type': 'application/json' } }
            );

            if (!response.ok) throw new Error('Failed to load approval records');

            let data = await response.json();
            if (!Array.isArray(data)) throw new Error('Invalid approval records response');

            if (this.state.searchKeyword && Array.isArray(data)) {
                const keyword = this.state.searchKeyword.toLowerCase();
                data = data.filter((item) => {
                    const partNo = (item.partNo || '').toLowerCase();
                    const lotNo = (item.lotNo || '').toLowerCase();
                    const location = (item.locationCode || item.fromLocationCode || '').toLowerCase();
                    const txn = (item.transactionId || '').toLowerCase();
                    const reason = (item.reason || item.scrapReason || '').toLowerCase();
                    return (
                        partNo.includes(keyword) ||
                        lotNo.includes(keyword) ||
                        location.includes(keyword) ||
                        txn.includes(keyword) ||
                        reason.includes(keyword)
                    );
                });
            }

            if (this.state.searchSubmittedDate && Array.isArray(data)) {
                const searchDate = normalizeDateFilterInput(this.state.searchSubmittedDate);
                if (!searchDate) {
                    data = [];
                } else {
                    data = data.filter((item) => {
                        if (!item.createdAt) return false;
                        return moment(item.createdAt).format('YYYY-MM-DD') === searchDate;
                    });
                }
            }

            if (Array.isArray(data)) {
                const formattedData = data.map((item, index) => {
                    const reportNo =
                        item.createdAt && item.id
                            ? moment(item.createdAt).format('YYYYMMDD') + 'S' + item.id
                            : item.id || '-';
                    const status = statusLabelScrap(item.status, item.rejectionReason);
                    return {
                        no: index + 1,
                        reportNo,
                        id: item.id,
                        partNo: item.partNo || '-',
                        description: item.description || '-',
                        lotNo: item.lotNo || '-',
                        qty: item.quantity || 0,
                        materialKind: item.materialKind || item.transferType || '-',
                        locationCode: item.locationCode || item.fromLocationCode || '-',
                        locationDescription:
                            item.locationDescription || item.fromLocationDescription || '',
                        reason: item.reason || item.scrapReason || '-',
                        submittedBy: item.createdBy || '-',
                        submittedDateTime: item.createdAt
                            ? moment(item.createdAt).format('DD/MM/YYYY HH:mm:ss')
                            : '-',
                        transactionId: item.transactionId || '-',
                        status,
                        approvedBy: item.approvedByName || '-',
                        approvedDateTime: formatDateTime(item.approvedAt),
                        sourceItem: item,
                    };
                });
                this.setState({ data: formattedData, loading: false });
            } else {
                this.setState({ data: [], loading: false });
            }
        } catch (error) {
            console.error('Error loading scrap data:', error);
            this.setState({
                data: [],
                loading: false,
                error: decisionSaved === true
                    ? 'Decision saved, but latest details could not be refreshed. Reload the page.'
                    : 'Approval records could not be loaded. Please refresh the page.',
            });
        }
    };

    handleSearch = () => {
        this.loadScrapData();
    };

    handleReset = () => {
        this.setState({ searchKeyword: '', searchSubmittedDate: '' }, () => {
            this.loadScrapData();
        });
    };

    exportExcel = () => {
        const rows = (this.state.data || []).map((row, index) =>
            mapScrappingToExportRow(row.sourceItem || row, index)
        );
        if (!rows.length) {
            alert('No records to export. Adjust filters or refresh the list.');
            return;
        }
        exportInventorySummary('scrapping-summary-export', 'Scrapping Summary', 'Scrapping_Summary');
    };

    getExportRows = () =>
        (this.state.data || []).map((row, index) =>
            mapScrappingToExportRow(row.sourceItem || row, index)
        );

    columns = [
        {
            name: 'Approval',
            cell: row => <ApprovalDecisionActions
                recordType={row.materialKind === 'FG' ? 'inventory_scrap_fg' : 'inventory_scrap_rm'}
                recordId={row.id}
                currentLevel={row.sourceItem?.approvalLevel}
                canApprove={row.sourceItem?.canApprove === true}
                canReject={row.sourceItem?.canApprove === true}
                onDecision={() => this.loadScrapData(true)}
            />,
            ignoreRowClick: true,
            width: '220px',
        },
        {
            name: 'No.',
            selector: (row) => row.no,
            width: '70px',
            sortable: true,
        },
        {
            name: 'Report No',
            selector: (row) => row.reportNo,
            width: '170px',
            sortable: true,
            cell: (row) => (
                <a
                    href="#"
                    style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (row.id) {
                            window.location.hash = `#/scrapping-summary/${row.id}`;
                        }
                    }}
                >
                    {row.reportNo}
                </a>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
        },
        {
            name: 'Part No',
            selector: (row) => row.partNo,
            width: '110px',
            sortable: true,
        },
        {
            name: 'Description',
            selector: (row) => row.description,
            width: '180px',
            sortable: true,
            wrap: true,
        },
        {
            name: 'Lot No',
            selector: (row) => row.lotNo,
            width: '110px',
            sortable: true,
        },
        {
            name: 'Qty',
            selector: (row) => row.qty,
            width: '70px',
            sortable: true,
            right: true,
        },
        {
            name: 'Type',
            selector: (row) => row.materialKind,
            width: '70px',
            sortable: true,
        },
        {
            name: 'Location',
            selector: (row) => row.locationCode,
            width: '130px',
            sortable: true,
            cell: (row) => (
                <div>
                    {row.locationCode}
                    {row.locationDescription ? (
                        <div style={{ fontSize: '11px', color: '#666' }}>{row.locationDescription}</div>
                    ) : null}
                </div>
            ),
            wrap: true,
        },
        {
            name: 'Reason',
            selector: (row) => row.reason,
            width: '160px',
            sortable: true,
            wrap: true,
        },
        {
            name: 'Submitted By',
            selector: (row) => row.submittedBy,
            width: '120px',
            sortable: true,
        },
        {
            name: 'Submitted Datetime',
            selector: (row) => row.submittedDateTime,
            width: '160px',
            sortable: true,
        },
        {
            name: 'Status',
            selector: (row) => row.status,
            width: '150px',
            sortable: true,
            wrap: true,
            cell: (row) => (
                <span
                    style={{
                        color: row.status.includes('Rejected')
                            ? '#dc3545'
                            : row.status === 'Approved'
                              ? '#28a745'
                              : row.status === 'Pending Approval'
                                ? '#ffc107'
                                : '#6c757d',
                        fontWeight: '500',
                    }}
                >
                    {row.status}
                </span>
            ),
        },
        {
            name: 'Approved By',
            selector: (row) => row.approvedBy,
            width: '130px',
            sortable: true,
        },
        {
            name: 'Approved Datetime',
            selector: (row) => row.approvedDateTime,
            width: '160px',
            sortable: true,
        },
    ];

    render() {
        const accessDenied = redirectUnlessInventory(localStorage.getItem('user'));
        if (accessDenied) return accessDenied;

        return (
            <div className="container" onFocusCapture={handleFocusInScrollField}>
                <div style={{ marginTop: '20px' }}>
                    <h4>Scrapping Summary</h4>
                    {this.state.error && <Alert variant="warning">{this.state.error}</Alert>}
                    <p className="text-muted">View scrap / variance records and approval details</p>

                    <div
                        style={{
                            marginBottom: '20px',
                            marginTop: '20px',
                            padding: '15px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '5px',
                        }}
                    >
                        <Form>
                            <Row className="align-items-end">
                                <Col sm={12} md={5}>
                                    <FormGroup>
                                        <FormControl
                                            type="text"
                                            placeholder="Search by Part No, Lot No, Location, Transaction ID, or Reason"
                                            value={this.state.searchKeyword}
                                            onChange={(e) =>
                                                this.setState({ searchKeyword: e.target.value })
                                            }
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm={12} md={3}>
                                    <FormGroup>
                                        <FormControl
                                            type="text"
                                            placeholder="Submitted Date (DD/MM/YYYY)"
                                            value={this.state.searchSubmittedDate}
                                            onChange={(e) =>
                                                this.setState({ searchSubmittedDate: e.target.value })
                                            }
                                        />
                                    </FormGroup>
                                </Col>
                                <Col sm={12} md={4}>
                                    <Button
                                        variant="primary"
                                        onClick={this.handleSearch}
                                        style={{ marginRight: '10px' }}
                                        disabled={this.state.loading}
                                    >
                                        Search
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={this.handleReset}
                                        style={{ marginRight: '10px' }}
                                        disabled={this.state.loading}
                                    >
                                        Reset
                                    </Button>
                                    <Button
                                        variant="info"
                                        onClick={this.loadScrapData}
                                        disabled={this.state.loading}
                                        style={{ marginRight: '10px' }}
                                    >
                                        {this.state.loading ? 'Loading...' : 'Refresh'}
                                    </Button>
                                    <Button
                                        variant="success"
                                        onClick={this.exportExcel}
                                        disabled={this.state.loading || !this.state.data.length}
                                    >
                                        Export Excel
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>

                    <SharedDataTable
                        columns={this.columns}
                        data={this.state.data}
                        progressPending={this.state.loading && !this.state.data.length}
                        progressComponent={<DatacoLoadingIndicator loading={this.state.loading} size="compact" label="Loading scrapping summary" />}
                        pagination
                        paginationPerPage={30}
                        paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
                        responsive
                        noDataComponent="No scrap records found"
                    />

                    <InventorySummaryExportTable
                        tableId="scrapping-summary-export"
                        columns={SCRAPPING_SUMMARY_EXPORT_COLUMNS}
                        rows={this.getExportRows()}
                    />
                </div>
            </div>
        );
    }
}

export { ScrappingSummary };
